import styled, { css } from 'styled-components';

const pillMain = '#7200E0';
const pillDropdown = '#5500B0';
const disabledBg = '#B2AEB7';
// Split-button values are lifted verbatim from the Figma frames (7641:286878 enabled,
// 7641:288958 disabled): #50009D arrow segment, #490888 divider (#918B97 when the
// arrow is disabled), 16px/500 text, 24px icons, 12px/7px paddings, no shadow.
const splitDropdown = '#50009D';
const splitDivider = '#490888';
const splitDisabledDivider = '#918B97';

export const ButtonContainer = styled.button`
  position: relative;
  z-index: 11;
  /* Only the single-button form blocks pointer events wholesale; the split form lets
     each half decide, so a disabled Submit still allows the dropdown. */
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  min-height: 40px;
  height: 40px;
  padding: 0;
  border-radius: 999px;
  border: none;
  box-shadow: ${({ disabled, $disabled, $split }) =>
    $split || disabled || $disabled ? 'none' : '0 1px 3px rgba(114, 0, 224, 0.25)'};
  font-family: var(--font-family-ibm_plex_sans);
  font-weight: ${({ $split }) => ($split ? 500 : 600)};
  font-size: var(--font-size-l);
  line-height: 24px;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  cursor: ${({ disabled, $disabled }) => (disabled || $disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled, $disabled }) => (disabled || $disabled ? disabledBg : pillMain)};
  /* the split form paints each half itself */
  overflow: hidden;
  flex-shrink: 0;

  @media screen and (max-width: 765px) {
    min-height: 36px;
    height: 36px;
  }
`;

export const MainPart = styled.span`
  border: none;
  font: inherit;
  color: inherit;
  cursor: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $split }) => ($split ? '4px' : '0')};
  padding: ${({ $split }) => ($split ? '0 12px' : '0 20px')};
  height: 100%;
  background: ${({ $hasDropdown, $disabled }) =>
    $disabled ? disabledBg : ($hasDropdown ? pillMain : 'transparent')
  };
  border-radius: ${({ $hasDropdown }) => ($hasDropdown ? '999px 0 0 999px' : '999px')};

  /* The feedback page carries two legacy resets this half must out-rank:
     ".cyMAeU button" (font-weight 600, border none, transparent bg) and
     ".cyMAeU .response-submit-btn-wrap button" (bg/gap/align !important,
     pointer-events none). Triple-& + !important wins on their own terms;
     pointer-events: auto re-enables hit-testing under the wrap's none. */
  ${({ $split, $disabled }) =>
    $split &&
    css`
      &&& {
        pointer-events: auto;
        font: inherit;
        border: none;
        align-items: center !important;
        gap: 4px !important;
        background: ${$disabled ? disabledBg : pillMain} !important;
      }
    `}

  @media screen and (max-width: 765px) {
    padding: ${({ $split }) => ($split ? '0 10px' : '0 16px')};
    font-size: 14px;
  }
`;

export const DropdownPart = styled.span`
  border: none;
  border-left: ${({ $split, $disabled }) =>
    $split
      ? `1px solid ${$disabled ? splitDisabledDivider : splitDivider}`
      : 'none'};
  font: inherit;
  color: inherit;
  cursor: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ $split }) => ($split ? '0' : '40px')};
  width: ${({ $split }) => ($split ? 'auto' : '40px')};
  height: 100%;
  padding: ${({ $split }) => ($split ? '0 7px' : '0')};
  background: ${({ $split, $disabled }) =>
    $disabled ? disabledBg : ($split ? splitDropdown : pillDropdown)};
  border-radius: 0 999px 999px 0;

  /* Same legacy-reset fight as MainPart; the divider needs restating because
     ".cyMAeU button" sets border: none at higher specificity than one class. */
  ${({ $split, $disabled }) =>
    $split &&
    css`
      &&& {
        pointer-events: auto;
        font: inherit;
        border: none;
        border-left: 1px solid ${$disabled ? splitDisabledDivider : splitDivider};
        /* Figma's divider is an inside stroke on the 38px segment, so the 1px
           border comes out of the left padding: 1+6+24+7 = 38. */
        padding: 0 7px 0 6px;
        align-items: center !important;
        background: ${$disabled ? disabledBg : splitDropdown} !important;
      }
    `}

  @media screen and (max-width: 765px) {
    padding: ${({ $split }) => ($split ? '0 6px' : '0')};
  }
`;

export const LeftImage = styled.img`
  width: ${({ $split }) => ($split ? '24px' : '20px')};
  height: ${({ $split }) => ($split ? '24px' : '20px')};
  margin-right: ${({ $split }) => ($split ? '0' : '6px')};
  filter: brightness(0) invert(1);
  image-rendering: crisp-edges;
  @media screen and (max-width: 765px) {
    width: ${({ $split }) => ($split ? '20px' : '18px')};
    height: ${({ $split }) => ($split ? '20px' : '18px')};
    margin-right: ${({ $split }) => ($split ? '0' : '4px')};
  }
`;

export const RightImage = styled.img`
  width: ${({ $split }) => ($split ? '24px' : '20px')};
  height: ${({ $split }) => ($split ? '24px' : '20px')};
  filter: brightness(0) invert(1);
  image-rendering: crisp-edges;
  @media screen and (max-width: 765px) {
    width: ${({ $split }) => ($split ? '20px' : '18px')};
    height: ${({ $split }) => ($split ? '20px' : '18px')};
  }
`;
